import { CounsellingIntakeAssessmentResponse, SaveCounsellingIntakeAssessmentRequest } from './assessment/cia/cia.model';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { InterventionApiService } from './intervention-api.service';
import {
  AssignInterventionAffiliateRequest,
  CreateInterventionRequest,
  CreateProviderReferralRequest,
  GrantInterventionConsentRequest,
  InterventionConsentResponse,
  InterventionConsentType,
  InterventionReportResponse,
  SubmitInterventionReportRequest,
  DeclineReferralRequest,
  InterventionAffiliateAssignmentResponse,
  InterventionListItemResponse,
  InterventionResponse,
  InterventionSessionAttendanceRequest,
  InterventionSessionRequest,
  InterventionSessionResponse,
  InterventionTaskResponse,
  InterventionTransactionHistoryResponse,
  InterventionTypeResponse,
  ProviderAssignmentResponse,
  ProviderReferralResponse,
  ReassignReferralRequest,
  EligibilityDependantRequest,
  InterventionProgressResponse,
  PresentingProblemCodingRequest,
  PresentingProblemCodingResponse,
  EligibilityMemberRequest,
  EligibilityResponse,
  ReferralPreferenceRequest,
  ReferrerRequest,
  ReferrerResponse,
  ReferralPreferenceResponse,
  EMPTY_REFERRAL_SUMMARY,
  ReferralSummaryRequest,
  ReferralSummaryResponse,
  RiskScreenerRequest,
  RiskScreenerResponse,
  SessionNoteRequest,
  SessionNoteResponse,
  UpdateInterventionRequest,
  WithdrawReferralRequest,
} from './intervention.model';
import {
  PAGE_REQUEST_DEFAULT_PAGE_NUMBER,
  PAGE_REQUEST_DEFAULT_PAGE_SIZE,
  PageRequest,
  normalizePageRequest,
} from '@core/models/common.model';
import { ToastService } from '@core/services/toast.service';

@Injectable({ providedIn: 'root' })
export class InterventionStore {
  private readonly api = inject(InterventionApiService);
  private readonly toast = inject(ToastService);

  readonly interventions = signal<InterventionListItemResponse[]>([]);
  readonly search = signal('');
  readonly totalCount = signal(0);
  readonly pageNumber = signal(PAGE_REQUEST_DEFAULT_PAGE_NUMBER);
  readonly pageSize = signal(PAGE_REQUEST_DEFAULT_PAGE_SIZE);
  readonly totalPages = signal(0);
  readonly selected = signal<InterventionResponse | null>(null);
  readonly interventionTypes = signal<InterventionTypeResponse[]>([]);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly typesLoading = signal(false);
  readonly history = signal<InterventionTransactionHistoryResponse[]>([]);
  readonly assigningAffiliate = signal(false);
  readonly currentAssignment = signal<InterventionAffiliateAssignmentResponse | null>(null);
  readonly referrals = signal<ProviderReferralResponse[]>([]);
  readonly referralsLoading = signal(false);
  readonly creatingReferral = signal(false);
  readonly respondingToReferral = signal(false);
  readonly lifecycleStages = signal<string[]>([]);
  readonly tasks = signal<InterventionTaskResponse[]>([]);
  readonly tasksLoading = signal(false);
  readonly completingTask = signal(false);
  readonly sessions = signal<InterventionSessionResponse[]>([]);
  readonly consents = signal<InterventionConsentResponse[]>([]);
  readonly reports = signal<InterventionReportResponse[]>([]);
  readonly reportsLoading = signal(false);
  readonly savingReport = signal(false);
  readonly referralSummary = signal<ReferralSummaryResponse>(EMPTY_REFERRAL_SUMMARY);
  readonly savingReferralSummary = signal(false);
  readonly referralPreference = signal<ReferralPreferenceResponse | null>(null);
  readonly savingReferralPreference = signal(false);
  readonly progress = signal<InterventionProgressResponse | null>(null);
  readonly presentingProblem = signal<PresentingProblemCodingResponse | null>(null);
  readonly savingPresentingProblem = signal(false);
  readonly referrer = signal<ReferrerResponse | null>(null);
  readonly savingReferrer = signal(false);
  readonly eligibility = signal<EligibilityResponse | null>(null);
  readonly savingEligibility = signal(false);
  readonly sessionNotes = signal<SessionNoteResponse[]>([]);
  readonly savingSessionNote = signal(false);
  readonly riskScreeners = signal<RiskScreenerResponse[]>([]);
  readonly riskScreenersLoading = signal(false);
  readonly savingRiskScreener = signal(false);
  readonly cia = signal<CounsellingIntakeAssessmentResponse | null>(null);
  readonly ciaLoading = signal(false);
  readonly savingCia = signal(false);
  readonly consentsLoading = signal(false);
  readonly savingConsent = signal(false);
  readonly sessionsLoading = signal(false);
  readonly savingSession = signal(false);

  // Emitted when another case is opened, so the reads of the case that was open that have not come back yet are dropped and cannot fill the new one.
  private readonly caseReset$ = new Subject<void>();

  // Another case is being opened: forget everything the case that was open held (the list, its paging and the types are kept).
  resetCase(): void {
    this.caseReset$.next();
    this.selected.set(null);
    this.history.set([]);
    this.currentAssignment.set(null);
    this.referrals.set([]);
    this.lifecycleStages.set([]);
    this.tasks.set([]);
    this.sessions.set([]);
    this.consents.set([]);
    this.reports.set([]);
    this.referralSummary.set(EMPTY_REFERRAL_SUMMARY);
    this.referralPreference.set(null);
    this.progress.set(null);
    this.presentingProblem.set(null);
    this.referrer.set(null);
    this.eligibility.set(null);
    this.sessionNotes.set([]);
    this.riskScreeners.set([]);
    this.cia.set(null);

    // A read that was dropped never gets to say it has finished.
    this.loading.set(false);
    this.tasksLoading.set(false);
    this.reportsLoading.set(false);
    this.referralsLoading.set(false);
    this.riskScreenersLoading.set(false);
    this.ciaLoading.set(false);
    this.consentsLoading.set(false);
    this.sessionsLoading.set(false);
  }

  load(pageRequest?: PageRequest, search?: string | null): void {
    const page = normalizePageRequest({
      pageNumber: pageRequest?.pageNumber ?? this.pageNumber(),
      pageSize: pageRequest?.pageSize ?? this.pageSize(),
    });
    if (search !== undefined) this.search.set(search?.trim() ?? '');
    this.loading.set(true);
    this.api.getInterventions(page, this.search()).subscribe({
      next: (response) => {
        this.interventions.set(response.items);
        this.totalCount.set(response.totalCount);
        this.pageNumber.set(response.pageNumber);
        this.pageSize.set(response.pageSize);
        this.totalPages.set(response.totalPages);
        this.loading.set(false);
      },
      error: (_: HttpErrorResponse) => {
        this.toast.error('Failed to load interventions');
        this.loading.set(false);
      },
    });
  }

  loadOne(id: string): void {
    this.loading.set(true);
    this.api.getIntervention(id).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (item) => {
        this.selected.set(item);
        this.loading.set(false);
        this.loadLifecycleStages(item.interventionTypeId);
        this.loadTasks(item.id);
      },
      error: (_: HttpErrorResponse) => {
        this.toast.error('Failed to load intervention');
        this.loading.set(false);
      },
    });
  }

  // Stage names in order for the intervention's type (Epic I / MW-126) - drives the progress
  // indicator and restricts the Status dropdown to the current stage plus its single valid next one.
  loadLifecycleStages(interventionTypeId: string): void {
    this.api.getLifecycleStages(interventionTypeId).subscribe({
      next: (stages) => this.lifecycleStages.set(stages),
      error: (_: HttpErrorResponse) => this.toast.error('Failed to load lifecycle stages'),
    });
  }

  // Epic K (MW-129/131) - every task for the intervention, not just the current stage's, so a
  // completed-stage history remains visible after the intervention moves on.
  loadTasks(interventionId: string): void {
    this.tasksLoading.set(true);
    this.api.getTasks(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (tasks) => {
        this.tasks.set(tasks);
        this.tasksLoading.set(false);
      },
      error: (_: HttpErrorResponse) => {
        this.toast.error('Failed to load intervention tasks');
        this.tasksLoading.set(false);
      },
    });
  }

  completeTask(interventionId: string, taskId: string, notes: string | null): void {
    this.completingTask.set(true);
    this.api.completeTask(interventionId, taskId, { notes }).subscribe({
      next: (task) => {
        this.tasks.set(this.tasks().map((item) => (item.id === task.id ? task : item)));
        this.toast.success('Task completed');
        this.completingTask.set(false);
        this.loadHistory(interventionId);
      },
      error: (_: HttpErrorResponse) => {
        this.completingTask.set(false);
      },
    });
  }

  // Reports and their approval (MW-142..147). A report can complete a task and an approval completes another, so
  // the task list is reloaded after each write; the server's specific reason for a refusal comes from the interceptor.
  loadReports(interventionId: string): void {
    this.reportsLoading.set(true);
    this.api.getReports(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (reports) => {
        this.reports.set(reports);
        this.reportsLoading.set(false);
      },
      error: (_: HttpErrorResponse) => this.reportsLoading.set(false),
    });
  }

  submitReport(interventionId: string, request: SubmitInterventionReportRequest, complete?: () => void): void {
    this.savingReport.set(true);
    this.api.submitReport(interventionId, request).subscribe({
      next: () => {
        this.toast.success('Report submitted');
        this.savingReport.set(false);
        this.afterReportChange(interventionId);
        complete?.();
      },
      error: (_: HttpErrorResponse) => this.savingReport.set(false),
    });
  }

  reviewReport(
    interventionId: string,
    reportId: string,
    action: 'approve' | 'return',
    notes: string | null,
    complete?: () => void,
  ): void {
    this.savingReport.set(true);
    this.api.reviewReport(interventionId, reportId, action, notes).subscribe({
      next: () => {
        this.toast.success(action === 'approve' ? 'Report approved' : 'Report returned');
        this.savingReport.set(false);
        this.afterReportChange(interventionId);
        complete?.();
      },
      error: (_: HttpErrorResponse) => this.savingReport.set(false),
    });
  }

  // A close-out or outcome report can move the intervention to its next stage, so it is re-read too.
  private afterReportChange(interventionId: string): void {
    this.loadReports(interventionId);
    this.loadHistory(interventionId);
    this.refreshIntervention(interventionId);
  }

  loadReferralSummary(interventionId: string): void {
    this.api.getReferralSummary(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (summary) => this.referralSummary.set(summary),
      error: (_: HttpErrorResponse) => undefined,
    });
  }

  saveReferralSummary(interventionId: string, request: ReferralSummaryRequest, complete?: () => void): void {
    this.savingReferralSummary.set(true);
    this.api.saveReferralSummary(interventionId, request).subscribe({
      next: (summary) => {
        this.toast.success('Treatment goals saved');
        this.referralSummary.set(summary);
        this.savingReferralSummary.set(false);
        // The goals can be what the assessment was waiting for to show Assessment Complete.
        this.loadOne(interventionId);
        this.loadHistory(interventionId);
        complete?.();
      },
      error: (_: HttpErrorResponse) => this.savingReferralSummary.set(false),
    });
  }

  loadReferralPreference(interventionId: string): void {
    this.api.getReferralPreference(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (preference) => this.referralPreference.set(preference),
      error: (_: HttpErrorResponse) => undefined,
    });
  }

  saveReferralPreference(interventionId: string, request: ReferralPreferenceRequest, complete?: () => void): void {
    this.savingReferralPreference.set(true);
    this.api.saveReferralPreference(interventionId, request).subscribe({
      next: (preference) => {
        this.toast.success('Referral preferences saved');
        this.referralPreference.set(preference);
        this.savingReferralPreference.set(false);
        this.loadHistory(interventionId);
        complete?.();
      },
      error: (_: HttpErrorResponse) => this.savingReferralPreference.set(false),
    });
  }

  loadPresentingProblem(interventionId: string): void {
    this.api.getPresentingProblem(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (coding) => this.presentingProblem.set(coding),
      error: (_: HttpErrorResponse) => undefined,
    });
  }

  savePresentingProblem(interventionId: string, request: PresentingProblemCodingRequest, complete?: () => void): void {
    this.savingPresentingProblem.set(true);
    this.api.savePresentingProblem(interventionId, request).subscribe({
      next: (coding) => {
        this.toast.success('Presenting problem saved');
        this.presentingProblem.set(coding);
        this.savingPresentingProblem.set(false);
        this.loadHistory(interventionId);
        complete?.();
      },
      error: (_: HttpErrorResponse) => this.savingPresentingProblem.set(false),
    });
  }

  loadReferrer(interventionId: string): void {
    this.api.getReferrer(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (referrer) => this.referrer.set(referrer),
      error: (_: HttpErrorResponse) => undefined,
    });
  }

  saveReferrer(interventionId: string, request: ReferrerRequest, complete?: () => void): void {
    this.savingReferrer.set(true);
    this.api.saveReferrer(interventionId, request).subscribe({
      next: (referrer) => {
        this.toast.success('Referrer saved');
        this.referrer.set(referrer);
        this.savingReferrer.set(false);
        this.loadHistory(interventionId);
        complete?.();
      },
      error: (_: HttpErrorResponse) => this.savingReferrer.set(false),
    });
  }

  removeReferrer(interventionId: string): void {
    this.savingReferrer.set(true);
    this.api.removeReferrer(interventionId).subscribe({
      next: (referrer) => {
        this.toast.success('Referrer removed');
        this.referrer.set(referrer);
        this.savingReferrer.set(false);
        this.loadHistory(interventionId);
      },
      error: (_: HttpErrorResponse) => this.savingReferrer.set(false),
    });
  }

  loadEligibility(interventionId: string): void {
    this.api.getEligibility(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (eligibility) => this.eligibility.set(eligibility),
      error: (_: HttpErrorResponse) => undefined,
    });
  }

  // Runs one save of the eligibility step (member, products, dependant or the decision) and shows the answer; a decision also reloads the case.
  private runEligibility(interventionId: string, call: Observable<EligibilityResponse>, message: string, complete?: () => void): void {
    this.savingEligibility.set(true);
    call.subscribe({
      next: (eligibility) => {
        this.toast.success(message);
        this.eligibility.set(eligibility);
        this.savingEligibility.set(false);
        this.loadHistory(interventionId);
        complete?.();
      },
      error: (_: HttpErrorResponse) => this.savingEligibility.set(false),
    });
  }

  saveEligibilityMember(interventionId: string, request: EligibilityMemberRequest, complete?: () => void): void {
    this.runEligibility(interventionId, this.api.saveEligibilityMember(interventionId, request), 'Member details saved', complete);
  }

  saveEligibilityProducts(interventionId: string, momentumProductIds: string[]): void {
    this.runEligibility(interventionId, this.api.saveEligibilityProducts(interventionId, momentumProductIds), 'Momentum products saved');
  }

  captureEligibilityDependant(interventionId: string, request: EligibilityDependantRequest, complete?: () => void): void {
    this.runEligibility(interventionId, this.api.captureEligibilityDependant(interventionId, request), 'Dependant captured', complete);
  }

  decideEligibility(interventionId: string, eligible: boolean, detail: string | null, complete?: () => void): void {
    this.runEligibility(
      interventionId,
      this.api.decideEligibility(interventionId, eligible, detail),
      eligible ? 'Member is eligible' : 'Member recorded as not eligible',
      () => {
        this.loadOne(interventionId);
        complete?.();
      },
    );
  }

  loadSessionNotes(interventionId: string): void {
    this.api.getSessionNotes(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (notes) => this.sessionNotes.set(notes),
      error: (_: HttpErrorResponse) => undefined,
    });
  }

  saveSessionNote(interventionId: string, sessionId: string, request: SessionNoteRequest, complete?: (note: SessionNoteResponse) => void): void {
    this.savingSessionNote.set(true);
    this.api.saveSessionNote(interventionId, sessionId, request).subscribe({
      next: (note) => {
        this.toast.success('Session notes saved');
        this.savingSessionNote.set(false);
        this.loadSessionNotes(interventionId);
        this.loadHistory(interventionId);
        complete?.(note);
      },
      error: (_: HttpErrorResponse) => this.savingSessionNote.set(false),
    });
  }

  // The clinical assessment (CIA) of the case. A case has none until the first save.
  loadCia(interventionId: string): void {
    this.ciaLoading.set(true);
    this.api.getCia(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (cia) => {
        this.cia.set(cia);
        this.ciaLoading.set(false);
      },
      error: (_: HttpErrorResponse) => {
        this.toast.error('Failed to load counselling intake assessment');
        this.ciaLoading.set(false);
      },
    });
  }

  // The first save creates the assessment and every later save updates it.
  saveCia(interventionId: string, request: SaveCounsellingIntakeAssessmentRequest, complete?: () => void, failed?: () => void): void {
    this.runCia(this.api.saveCia(interventionId, request), 'Counselling intake assessment saved successfully', complete, failed);
  }

  finalizeCia(interventionId: string, complete?: () => void, failed?: () => void): void {
    this.runCia(this.api.finalizeCia(interventionId), 'Assessment locked and the case advanced to referral', complete, failed);
  }

  reopenCia(interventionId: string, complete?: () => void): void {
    this.runCia(this.api.reopenCia(interventionId), 'Counselling intake assessment reopened', complete);
  }

  private runCia(call: Observable<CounsellingIntakeAssessmentResponse>, message: string, complete?: () => void, failed?: () => void): void {
    this.savingCia.set(true);
    call.subscribe({
      next: (cia) => {
        this.cia.set(cia);
        this.toast.success(message);
        this.savingCia.set(false);
        complete?.();
      },
      error: (_: HttpErrorResponse) => {
        this.savingCia.set(false);
        failed?.();
      },
    });
  }

  loadRiskScreeners(interventionId: string): void {
    this.riskScreenersLoading.set(true);
    this.api.getRiskScreeners(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (screeners) => {
        this.riskScreeners.set(screeners);
        this.riskScreenersLoading.set(false);
      },
      error: (_: HttpErrorResponse) => this.riskScreenersLoading.set(false),
    });
  }

  // A screener can complete the type's risk-assessment task and sets the intervention's risk classification.
  createRiskScreener(interventionId: string, request: RiskScreenerRequest, complete?: (screener: RiskScreenerResponse) => void): void {
    this.savingRiskScreener.set(true);
    this.api.createRiskScreener(interventionId, request).subscribe({
      next: (screener) => {
        this.toast.success('Risk screener recorded');
        this.savingRiskScreener.set(false);
        this.loadRiskScreeners(interventionId);
        this.loadTasks(interventionId);
        this.loadHistory(interventionId);
        this.refreshIntervention(interventionId);
        complete?.(screener);
      },
      error: (_: HttpErrorResponse) => this.savingRiskScreener.set(false),
    });
  }

  // L1 (MW-132). Whether this type of consent is in force: the latest version, neither withdrawn nor expired.
  // A referral needs referral consent and a session needs service consent; the server enforces both.
  hasConsent(consentType: InterventionConsentType): boolean {
    return this.consents().some((consent) => consent.consentType === consentType && consent.status === 'Active');
  }

  // The member's latest answer for this consent is a refusal.
  isConsentDeclined(consentType: InterventionConsentType): boolean {
    return this.consents().some((consent) => consent.consentType === consentType && consent.status === 'Declined');
  }

  loadConsents(interventionId: string): void {
    this.consentsLoading.set(true);
    this.api.getConsents(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (consents) => {
        this.consents.set(consents);
        this.consentsLoading.set(false);
      },
      error: (_: HttpErrorResponse) => this.consentsLoading.set(false),
    });
  }

  // Recording consent completes the matching "Obtain ... consent" task, so the task list is reloaded too.
  grantConsent(interventionId: string, request: GrantInterventionConsentRequest, complete?: () => void): void {
    this.savingConsent.set(true);
    this.api.grantConsent(interventionId, request).subscribe({
      next: () => {
        this.toast.success('Consent recorded');
        this.savingConsent.set(false);
        this.loadConsents(interventionId);
        this.loadTasks(interventionId);
        this.loadHistory(interventionId);
        complete?.();
      },
      error: (_: HttpErrorResponse) => this.savingConsent.set(false),
    });
  }

  withdrawConsent(interventionId: string, consentId: string, reason: string, complete?: () => void): void {
    this.savingConsent.set(true);
    this.api.withdrawConsent(interventionId, consentId, reason).subscribe({
      next: () => {
        this.toast.success('Consent withdrawn');
        this.savingConsent.set(false);
        this.loadConsents(interventionId);
        this.loadHistory(interventionId);
        complete?.();
      },
      error: (_: HttpErrorResponse) => this.savingConsent.set(false),
    });
  }

  // N1 (MW-139). No generic error toast on the writes below: the problemDetailsInterceptor already shows the
  // server's specific reason (for example the case's session limit being reached).
  loadSessions(interventionId: string): void {
    this.sessionsLoading.set(true);
    this.api.getSessions(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (sessions) => {
        this.sessions.set(sessions);
        this.sessionsLoading.set(false);
      },
      error: (_: HttpErrorResponse) => this.sessionsLoading.set(false),
    });
  }

  // Schedules a new session, or updates the one named by sessionId.
  saveSession(
    interventionId: string,
    sessionId: string | null,
    request: InterventionSessionRequest,
    complete?: () => void,
  ): void {
    this.savingSession.set(true);
    const call = sessionId
      ? this.api.updateSession(interventionId, sessionId, request)
      : this.api.scheduleSession(interventionId, request);

    call.subscribe({
      next: () => {
        this.toast.success(sessionId ? 'Session updated' : 'Session scheduled');
        this.savingSession.set(false);
        this.loadSessions(interventionId);
        this.loadHistory(interventionId);
        complete?.();
      },
      error: (_: HttpErrorResponse) => this.savingSession.set(false),
    });
  }

  cancelSession(interventionId: string, sessionId: string): void {
    this.savingSession.set(true);
    this.api.cancelSession(interventionId, sessionId).subscribe({
      next: () => {
        this.toast.success('Session cancelled');
        this.savingSession.set(false);
        this.loadSessions(interventionId);
        this.loadHistory(interventionId);
      },
      error: (_: HttpErrorResponse) => this.savingSession.set(false),
    });
  }

  // N2 (MW-140). Recording attendance can complete the attendance task, so the task list is reloaded too.
  captureAttendance(
    interventionId: string,
    sessionId: string,
    request: InterventionSessionAttendanceRequest,
    complete?: () => void,
  ): void {
    this.savingSession.set(true);
    this.api.captureAttendance(interventionId, sessionId, request).subscribe({
      next: () => {
        this.toast.success('Attendance recorded');
        this.savingSession.set(false);
        this.loadSessions(interventionId);
        this.loadTasks(interventionId);
        this.loadHistory(interventionId);
        complete?.();
      },
      error: (_: HttpErrorResponse) => this.savingSession.set(false),
    });
  }

  verifyAttendance(interventionId: string, sessionId: string): void {
    this.savingSession.set(true);
    this.api.verifyAttendance(interventionId, sessionId).subscribe({
      next: () => {
        this.toast.success('Attendance verified');
        this.savingSession.set(false);
        this.loadSessions(interventionId);
        this.loadHistory(interventionId);
      },
      error: (_: HttpErrorResponse) => this.savingSession.set(false),
    });
  }

  loadInterventionTypes(): void {
    this.typesLoading.set(true);
    this.api.getInterventionTypes().subscribe({
      next: (types) => {
        this.interventionTypes.set(types);
        this.typesLoading.set(false);
      },
      error: (_: HttpErrorResponse) => {
        this.toast.error('Failed to load intervention types');
        this.typesLoading.set(false);
      },
    });
  }

  create(request: CreateInterventionRequest, complete?: (item: InterventionResponse) => void): void {
    this.saving.set(true);
    this.api.createIntervention(request).subscribe({
      next: (item) => {
        this.selected.set(item);
        this.toast.success('Intervention created successfully');
        this.saving.set(false);
        complete?.(item);
      },
      error: (_: HttpErrorResponse) => {
        this.saving.set(false);
      },
    });
  }

  update(
    id: string,
    request: UpdateInterventionRequest,
    complete?: (item: InterventionResponse) => void,
  ): void {
    this.saving.set(true);
    this.api.updateIntervention(id, request).subscribe({
      next: (item) => {
        this.selected.set(item);
        this.toast.success('Intervention updated successfully');
        this.saving.set(false);
        this.loadHistory(id);
        this.loadLifecycleStages(item.interventionTypeId);
        this.loadTasks(item.id);
        complete?.(item);
      },
      // No generic toast here - the global problemDetailsInterceptor already surfaces the specific
      // reason (e.g. K2's "required task(s) incomplete" message), which a generic one would bury.
      error: (_: HttpErrorResponse) => {
        this.saving.set(false);
      },
    });
  }

  // The wizard's progress. It is read again whenever the history is, since a step that saved something has written to the history.
  loadProgress(interventionId: string): void {
    this.api.getProgress(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (progress) => this.progress.set(progress),
      // Without its progress the case still opens (at the first step), so the page is not left waiting for it.
      error: (_: HttpErrorResponse) => this.progress.set({ interventionId, steps: [], nextStepKey: null, context: null }),
    });
  }

  loadHistory(interventionId: string): void {
    this.loadProgress(interventionId);
    this.api.getInterventionHistory(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (history) => this.history.set(history),
      error: (_: HttpErrorResponse) => this.toast.error('Failed to load intervention history'),
    });
  }

  assignAffiliate(
    interventionId: string,
    request: AssignInterventionAffiliateRequest,
    complete?: (assignment: InterventionAffiliateAssignmentResponse) => void,
  ): void {
    this.assigningAffiliate.set(true);
    this.api.assignAffiliate(interventionId, request).subscribe({
      next: (assignment) => {
        this.currentAssignment.set(assignment);
        this.toast.success('Affiliate assigned successfully');
        this.assigningAffiliate.set(false);
        complete?.(assignment);
      },
      error: (_: HttpErrorResponse) => {
        this.toast.error('Failed to assign affiliate');
        this.assigningAffiliate.set(false);
      },
    });
  }

  loadReferrals(interventionId: string): void {
    this.referralsLoading.set(true);
    this.api.getReferrals(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (referrals) => {
        this.referrals.set(referrals);
        this.referralsLoading.set(false);
      },
      error: (_: HttpErrorResponse) => {
        this.toast.error('Failed to load referrals');
        this.referralsLoading.set(false);
      },
    });
  }

  createReferral(
    interventionId: string,
    request: CreateProviderReferralRequest,
    complete?: (referral: ProviderReferralResponse) => void,
  ): void {
    this.creatingReferral.set(true);
    this.api.createReferral(interventionId, request).subscribe({
      next: (referral) => {
        this.referrals.set([referral, ...this.referrals()]);
        this.toast.success('Referral created successfully');
        this.creatingReferral.set(false);
        this.loadHistory(interventionId);
        // J1 auto-advances Intervention.Status as a side effect of this call - re-fetch so the
        // stepper/status dropdown (and the task panel's current-stage list) reflect it.
        this.refreshIntervention(interventionId);
        complete?.(referral);
      },
      error: (_: HttpErrorResponse) => {
        this.toast.error('Failed to create referral');
        this.creatingReferral.set(false);
      },
    });
  }

  acceptReferral(interventionId: string, referralId: string, affiliateId: string): void {
    this.respondingToReferral.set(true);
    this.api.acceptReferral(referralId, affiliateId).subscribe({
      next: (assignment) => {
        this.applyAssignmentToReferral(referralId, assignment);
        this.toast.success('Referral accepted');
        this.respondingToReferral.set(false);
        this.loadHistory(interventionId);
        // Same as createReferral - J1 advances Status to ServiceDelivery here too.
        this.refreshIntervention(interventionId);
      },
      error: (_: HttpErrorResponse) => {
        this.toast.error('Failed to accept referral');
        this.respondingToReferral.set(false);
      },
    });
  }

  // Re-fetches the intervention in place (no loading spinner, unlike loadOne) - used after an action
  // on a sub-resource (a referral) that can change Intervention.Status as a side effect.
  private refreshIntervention(interventionId: string): void {
    this.api.getIntervention(interventionId).pipe(takeUntil(this.caseReset$)).subscribe({
      next: (item) => {
        this.selected.set(item);
        this.loadLifecycleStages(item.interventionTypeId);
        this.loadTasks(item.id);
      },
      error: (_: HttpErrorResponse) => this.toast.error('Failed to refresh intervention'),
    });
  }

  // Unlike accept, decline/withdraw drop the intervention off the affiliate's own scope (see
  // InterventionRepository.ApplyTenantScope) - they're never staff actions in this UI, so there's no
  // history view left to refresh; the caller navigates the affiliate away instead.
  declineReferral(
    referralId: string,
    affiliateId: string,
    request: DeclineReferralRequest,
    complete?: () => void,
  ): void {
    this.respondingToReferral.set(true);
    this.api.declineReferral(referralId, affiliateId, request).subscribe({
      next: (assignment) => {
        this.applyAssignmentToReferral(referralId, assignment);
        this.toast.success('Referral declined');
        this.respondingToReferral.set(false);
        complete?.();
      },
      error: (_: HttpErrorResponse) => {
        this.toast.error('Failed to decline referral');
        this.respondingToReferral.set(false);
      },
    });
  }

  withdrawReferral(
    referralId: string,
    affiliateId: string,
    request: WithdrawReferralRequest,
    complete?: () => void,
  ): void {
    this.respondingToReferral.set(true);
    this.api.withdrawReferral(referralId, affiliateId, request).subscribe({
      next: (assignment) => {
        this.applyAssignmentToReferral(referralId, assignment);
        this.toast.success('Acceptance withdrawn');
        this.respondingToReferral.set(false);
        complete?.();
      },
      error: (_: HttpErrorResponse) => {
        this.toast.error('Failed to withdraw from referral');
        this.respondingToReferral.set(false);
      },
    });
  }

  reassignReferral(interventionId: string, referralId: string, request: ReassignReferralRequest): void {
    this.respondingToReferral.set(true);
    this.api.reassignReferral(referralId, request).subscribe({
      next: (referral) => {
        this.referrals.set(this.referrals().map((item) => (item.id === referralId ? referral : item)));
        this.toast.success('Referral reassigned');
        this.respondingToReferral.set(false);
        this.loadHistory(interventionId);
      },
      error: (_: HttpErrorResponse) => {
        this.toast.error('Failed to reassign referral');
        this.respondingToReferral.set(false);
      },
    });
  }

  // Accept/decline/withdraw only return the single affected assignment, not the whole referral -
  // merge it into the cached list rather than refetching.
  private applyAssignmentToReferral(referralId: string, assignment: ProviderAssignmentResponse): void {
    this.referrals.set(
      this.referrals().map((referral) => {
        if (referral.id !== referralId) {
          return referral;
        }

        const assignments = referral.assignments.some((item) => item.id === assignment.id)
          ? referral.assignments.map((item) => (item.id === assignment.id ? assignment : item))
          : [...referral.assignments, assignment];

        // Accepting settles the referral itself, so its own status moves with the assignment instead of staying Pending until a reload.
        const acceptanceStatus = assignment.assignmentStatus === 'Accepted' ? 'Accepted' : referral.acceptanceStatus;

        return { ...referral, assignments, acceptanceStatus };
      }),
    );
  }
}
